import { useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useTranslations } from "@/i18n/IntlProvider";

/**
 * /$locale/admin/accounts/$id — system-admin operator detail view.
 *
 * ADR-027 sub-iter #5 port of web/src/components/admin/admin-account-detail.tsx.
 * Same-origin cookie auth via @/lib/api; the role-guard on the route in
 * router.tsx makes sure non-admins never get this far. Wires the
 * /v1/admin endpoints from AccountAdminController (identity) and
 * BillingAdminController (billing).
 */
type AdminAccount = {
  id: string;
  displayName: string;
  primaryEmail: string;
  createdAt: string;
  archivedAt: string | null;
  suspended: boolean;
};

type AdminPlan = {
  id: string;
  name: string;
  billingCycle: string;
};

type OverrideKind = "TOGGLE" | "QUOTA" | "ENUM";

type AdminSubscriptionOverride = {
  kind: OverrideKind;
  value: boolean | number | string | null;
};

type AdminSubscription = {
  id: string;
  accountId: string;
  planId: string;
  status: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  overrides: Record<string, AdminSubscriptionOverride>;
};

type AdminEntitlements = {
  planId: string;
  toggles: Record<string, boolean>;
  quotas: Record<string, number | null>;
  enums: Record<string, string>;
};

type OverrideInput = {
  feature: string;
  kind: OverrideKind;
  value: boolean | number | string | null;
};

const QK = {
  account: (id: string) => ["admin", "account", id] as const,
  subscription: (id: string) => ["admin", "subscription", id] as const,
  entitlements: (id: string) => ["admin", "entitlements", id] as const,
  plans: ["admin", "plans"] as const,
};

export function AdminAccountDetailRoute() {
  const { locale, id } = useParams({ strict: false }) as { locale?: string; id?: string };
  const lang = locale ?? "de";
  const accountId = id ?? "";
  const t = useTranslations();
  const queryClient = useQueryClient();

  const accountQuery = useQuery({
    queryKey: QK.account(accountId),
    queryFn: () => api<AdminAccount>(`/v1/admin/accounts/${accountId}`),
    enabled: accountId.length > 0,
  });
  const subscriptionQuery = useQuery({
    queryKey: QK.subscription(accountId),
    queryFn: () => api<AdminSubscription | null>(`/v1/admin/accounts/${accountId}/subscription`),
    enabled: accountId.length > 0,
  });
  const entitlementsQuery = useQuery({
    queryKey: QK.entitlements(accountId),
    queryFn: () => api<AdminEntitlements>(`/v1/admin/accounts/${accountId}/entitlements`),
    enabled: accountId.length > 0,
  });
  const plansQuery = useQuery({
    queryKey: QK.plans,
    queryFn: () => api<AdminPlan[]>("/v1/admin/plans"),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: QK.account(accountId) });
    void queryClient.invalidateQueries({ queryKey: QK.subscription(accountId) });
    void queryClient.invalidateQueries({ queryKey: QK.entitlements(accountId) });
  };

  const account = accountQuery.data;
  const subscription = subscriptionQuery.data ?? null;
  const entitlements = entitlementsQuery.data;
  const plans = plansQuery.data ?? [];

  return (
    <article className="mx-auto max-w-screen-xl space-y-6">
      <Link
        to={"/$locale/admin/accounts" as never}
        params={{ locale: lang } as never}
        className="text-sm text-muted-foreground hover:text-foreground"
      >
        ← {t("app.admin.detail.back")}
      </Link>

      {accountQuery.isPending && (
        <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
      )}
      {accountQuery.isError && (
        <p className="text-sm text-destructive">
          {t("common.error")}: {(accountQuery.error as Error).message}
        </p>
      )}
      {account && <AccountHeader account={account} onChange={invalidate} />}

      <SubscriptionSection
        accountId={accountId}
        subscription={subscription}
        plans={plans}
        loading={subscriptionQuery.isPending || plansQuery.isPending}
        onChange={invalidate}
      />

      {entitlements && subscription && (
        <OverridesSection
          subscription={subscription}
          entitlements={entitlements}
          accountId={accountId}
          onChange={invalidate}
        />
      )}

      {entitlements && <EntitlementsSection entitlements={entitlements} />}
    </article>
  );
}

function AccountHeader({
  account,
  onChange,
}: {
  account: AdminAccount;
  onChange: () => void;
}) {
  const t = useTranslations();
  const suspendMut = useMutation({
    mutationFn: () =>
      api<AdminAccount>(
        `/v1/admin/accounts/${account.id}/${account.suspended ? "unsuspend" : "suspend"}`,
        { method: "POST" },
      ),
    onSuccess: onChange,
  });
  const deleteMut = useMutation({
    mutationFn: () => api<void>(`/v1/admin/accounts/${account.id}`, { method: "DELETE" }),
    onSuccess: onChange,
  });

  return (
    <header className="rounded-md border border-border bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{account.displayName}</h2>
          <p className="text-sm text-muted-foreground">{account.primaryEmail}</p>
          <p className="mt-1 font-mono text-xs text-muted-foreground">{account.id}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {t("app.admin.detail.created")}: {new Date(account.createdAt).toLocaleString()}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => {
              if (
                account.suspended ||
                window.confirm(
                  t("app.admin.detail.suspendConfirm").replace("{email}", account.primaryEmail),
                )
              ) {
                suspendMut.mutate();
              }
            }}
            disabled={suspendMut.isPending}
            className={
              "inline-flex h-9 items-center rounded-md border px-3 text-sm font-medium " +
              (account.suspended
                ? "border-emerald-300 bg-emerald-50 text-emerald-900 hover:bg-emerald-100"
                : "border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100")
            }
          >
            {account.suspended ? t("app.admin.detail.resume") : t("app.admin.detail.suspend")}
          </button>
          <button
            type="button"
            onClick={() => {
              if (
                window.confirm(
                  t("app.admin.detail.deleteConfirm").replace("{email}", account.primaryEmail),
                )
              ) {
                deleteMut.mutate();
              }
            }}
            disabled={deleteMut.isPending}
            className="inline-flex h-9 items-center rounded-md border border-rose-300 bg-rose-50 px-3 text-sm font-medium text-rose-900 hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("app.admin.detail.hardDelete")}
          </button>
        </div>
      </div>
      {suspendMut.isError && (
        <p className="mt-2 text-sm text-destructive">{(suspendMut.error as Error).message}</p>
      )}
      {deleteMut.isSuccess && (
        <p className="mt-2 text-sm text-emerald-700">{t("app.admin.detail.deleted")}</p>
      )}
    </header>
  );
}

function SubscriptionSection({
  accountId,
  subscription,
  plans,
  loading,
  onChange,
}: {
  accountId: string;
  subscription: AdminSubscription | null;
  plans: readonly AdminPlan[];
  loading: boolean;
  onChange: () => void;
}) {
  const t = useTranslations();
  const [selectedPlan, setSelectedPlan] = useState<string>("");

  const assignMut = useMutation({
    mutationFn: (planId: string) =>
      api<AdminSubscription>(`/v1/admin/accounts/${accountId}/subscription`, {
        method: "POST",
        body: JSON.stringify({ planId }),
      }),
    onSuccess: onChange,
  });
  const cancelMut = useMutation({
    mutationFn: () =>
      api<AdminSubscription | null>(`/v1/admin/accounts/${accountId}/subscription`, {
        method: "DELETE",
      }),
    onSuccess: onChange,
  });

  const currentPlan = subscription?.planId ?? "";
  const fallbackSelection = currentPlan || plans[0]?.id || "";
  const effectiveSelection = selectedPlan || fallbackSelection;

  return (
    <section className="rounded-md border border-border bg-card p-4">
      <h2 className="text-lg font-semibold">{t("app.admin.detail.subscription.title")}</h2>
      {loading && (
        <p className="mt-2 text-sm text-muted-foreground">{t("common.loading")}</p>
      )}
      {!loading && subscription && (
        <dl className="mt-3 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
          <Row label={t("app.admin.detail.subscription.plan")} value={subscription.planId} />
          <Row label={t("app.admin.detail.subscription.status")} value={subscription.status} />
          <Row
            label={t("app.admin.detail.subscription.periodStart")}
            value={new Date(subscription.currentPeriodStart).toLocaleString()}
          />
          <Row
            label={t("app.admin.detail.subscription.periodEnd")}
            value={new Date(subscription.currentPeriodEnd).toLocaleString()}
          />
          <Row
            label={t("app.admin.detail.subscription.id")}
            value={<span className="font-mono text-xs">{subscription.id}</span>}
          />
        </dl>
      )}
      {!loading && !subscription && (
        <p className="mt-2 text-sm text-muted-foreground">
          {t("app.admin.detail.subscription.none")}
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-border pt-3">
        <div>
          <label
            className="block text-xs font-medium text-muted-foreground"
            htmlFor="plan-picker"
          >
            {t("app.admin.detail.subscription.assignLabel")}
          </label>
          <select
            id="plan-picker"
            className="mt-1 h-9 w-56 rounded-md border border-border bg-background px-2 text-sm"
            value={effectiveSelection}
            onChange={(e) => setSelectedPlan(e.target.value)}
          >
            {plans.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.id})
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          disabled={!effectiveSelection || assignMut.isPending}
          onClick={() => assignMut.mutate(effectiveSelection)}
          className="inline-flex h-9 items-center rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {subscription
            ? t("app.admin.detail.subscription.changePlan")
            : t("app.admin.detail.subscription.create")}
        </button>
        {subscription && (
          <button
            type="button"
            disabled={cancelMut.isPending}
            onClick={() => {
              if (window.confirm(t("app.admin.detail.subscription.cancelConfirm"))) {
                cancelMut.mutate();
              }
            }}
            className="inline-flex h-9 items-center rounded-md border border-rose-300 bg-rose-50 px-3 text-sm font-medium text-rose-900 hover:bg-rose-100"
          >
            {t("app.admin.detail.subscription.cancel")}
          </button>
        )}
      </div>
      {assignMut.isError && (
        <p className="mt-2 text-sm text-destructive">{(assignMut.error as Error).message}</p>
      )}
      {cancelMut.isError && (
        <p className="mt-2 text-sm text-destructive">{(cancelMut.error as Error).message}</p>
      )}
    </section>
  );
}

function OverridesSection({
  subscription,
  entitlements,
  accountId,
  onChange,
}: {
  subscription: AdminSubscription;
  entitlements: AdminEntitlements;
  accountId: string;
  onChange: () => void;
}) {
  const t = useTranslations();
  const [feature, setFeature] = useState("");
  const [kind, setKind] = useState<OverrideKind>("TOGGLE");
  const [rawValue, setRawValue] = useState("true");

  const setMut = useMutation({
    mutationFn: (input: OverrideInput) =>
      api<AdminSubscription>(`/v1/admin/subscriptions/${subscription.id}/overrides`, {
        method: "PUT",
        body: JSON.stringify(input),
      }),
    onSuccess: onChange,
  });
  const removeMut = useMutation({
    mutationFn: (key: string) =>
      api<AdminSubscription>(
        `/v1/admin/subscriptions/${subscription.id}/overrides/${encodeURIComponent(key)}`,
        { method: "DELETE" },
      ),
    onSuccess: onChange,
  });
  const resetMut = useMutation({
    mutationFn: (key: string) =>
      api<{ feature: string; previousUsage: number }>(
        `/v1/admin/accounts/${accountId}/quota-reset`,
        { method: "POST", body: JSON.stringify({ feature: key }) },
      ),
    onSuccess: onChange,
  });

  const overrideEntries = Object.entries(subscription.overrides);

  return (
    <section className="rounded-md border border-border bg-card p-4">
      <h2 className="text-lg font-semibold">{t("app.admin.detail.overrides.title")}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{t("app.admin.detail.overrides.desc")}</p>

      {overrideEntries.length > 0 ? (
        <table className="mt-3 w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">{t("app.admin.detail.overrides.feature")}</th>
              <th className="px-3 py-2 text-left">{t("app.admin.detail.overrides.kind")}</th>
              <th className="px-3 py-2 text-left">{t("app.admin.detail.overrides.value")}</th>
              <th className="px-3 py-2 text-left">{t("app.admin.detail.overrides.action")}</th>
            </tr>
          </thead>
          <tbody>
            {overrideEntries.map(([key, override]) => (
              <tr key={key} className="border-t border-border">
                <td className="px-3 py-2 font-mono text-xs">{key}</td>
                <td className="px-3 py-2">{override.kind}</td>
                <td className="px-3 py-2">{String(override.value)}</td>
                <td className="px-3 py-2">
                  <button
                    type="button"
                    onClick={() => removeMut.mutate(key)}
                    disabled={removeMut.isPending}
                    className="text-xs text-rose-700 hover:underline"
                  >
                    {t("app.admin.detail.overrides.remove")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="mt-3 text-sm text-muted-foreground">
          {t("app.admin.detail.overrides.empty")}
        </p>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!feature.trim()) return;
          setMut.mutate({
            feature: feature.trim(),
            kind,
            value: parseOverrideValue(kind, rawValue),
          });
        }}
        className="mt-4 grid grid-cols-1 gap-2 border-t border-border pt-3 sm:grid-cols-[2fr_1fr_2fr_auto]"
      >
        <input
          type="text"
          placeholder={t("app.admin.detail.overrides.featurePlaceholder")}
          value={feature}
          onChange={(e) => setFeature(e.target.value)}
          className="h-9 rounded-md border border-border bg-background px-2 text-sm"
        />
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value as OverrideKind)}
          className="h-9 rounded-md border border-border bg-background px-2 text-sm"
        >
          <option value="TOGGLE">TOGGLE</option>
          <option value="QUOTA">QUOTA</option>
          <option value="ENUM">ENUM</option>
        </select>
        <input
          type="text"
          placeholder={
            kind === "TOGGLE"
              ? t("app.admin.detail.overrides.valuePlaceholderToggle")
              : kind === "QUOTA"
                ? t("app.admin.detail.overrides.valuePlaceholderQuota")
                : t("app.admin.detail.overrides.valuePlaceholderEnum")
          }
          value={rawValue}
          onChange={(e) => setRawValue(e.target.value)}
          className="h-9 rounded-md border border-border bg-background px-2 text-sm"
        />
        <button
          type="submit"
          disabled={setMut.isPending || !feature.trim()}
          className="inline-flex h-9 items-center rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {t("app.admin.detail.overrides.set")}
        </button>
      </form>
      {setMut.isError && (
        <p className="mt-2 text-sm text-destructive">{(setMut.error as Error).message}</p>
      )}

      <QuotaUsageBlock
        accountId={accountId}
        entitlements={entitlements}
        resetMut={resetMut}
      />
    </section>
  );
}

type QuotaUsageRow = {
  feature: string;
  limit: number | null;
  used: number;
  periodStart: string | null;
};

type QuotaUsageResponse = {
  usage: QuotaUsageRow[];
};

/**
 * Quota usage + reset row. Pulls live counters from
 * `/v1/admin/accounts/{id}/quota-usage` so the operator sees the
 * "used / limit" figure before deciding whether to reset. Limits-only
 * features (`null` limit, e.g. enums hosted in the same map) are
 * filtered out — the reset button is meaningless for them.
 */
function QuotaUsageBlock({
  accountId,
  entitlements,
  resetMut,
}: {
  accountId: string;
  entitlements: AdminEntitlements;
  resetMut: ReturnType<
    typeof useMutation<{ feature: string; previousUsage: number }, Error, string>
  >;
}) {
  const t = useTranslations();
  const queryClient = useQueryClient();
  const usageQuery = useQuery({
    queryKey: ["admin", "quota-usage", accountId],
    queryFn: () => api<QuotaUsageResponse>(`/v1/admin/accounts/${accountId}/quota-usage`),
    retry: false,
    staleTime: 15_000,
  });

  // Refresh both the entitlements query (limit changes via overrides)
  // and the quota-usage query (the just-reset counter) after a reset.
  const onResetSuccess = () => {
    void queryClient.invalidateQueries({
      queryKey: ["admin", "quota-usage", accountId],
    });
  };

  const usageByFeature = new Map(
    (usageQuery.data?.usage ?? []).map((r) => [r.feature, r]),
  );
  // The entitlements.quotas map is the source of truth for which features
  // are quota-typed; usage rows fall in as we get them. Stable order =
  // sorted alphabetically so the table doesn't reshuffle between
  // refetches.
  const features = Object.keys(entitlements.quotas).sort((a, b) =>
    a.localeCompare(b),
  );

  return (
    <div className="mt-4 border-t border-border pt-3">
      <h3 className="text-sm font-semibold">{t("app.admin.detail.quota.title")}</h3>
      <p className="text-xs text-muted-foreground">{t("app.admin.detail.quota.desc")}</p>

      {features.length === 0 ? (
        <p className="mt-2 text-xs text-muted-foreground">
          {t("app.admin.detail.quota.empty")}
        </p>
      ) : (
        <div className="mt-2 overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">
                  {t("app.admin.detail.quota.colFeature")}
                </th>
                <th className="px-3 py-2 text-right">
                  {t("app.admin.detail.quota.colUsage")}
                </th>
                <th className="px-3 py-2 text-right" />
              </tr>
            </thead>
            <tbody>
              {features.map((key) => {
                const row = usageByFeature.get(key);
                const limit = row?.limit ?? entitlements.quotas[key] ?? null;
                const used = row?.used ?? 0;
                return (
                  <tr key={key} className="border-t border-border">
                    <td className="px-3 py-2 font-mono text-xs">{key}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {limit === null
                        ? `${used.toLocaleString()} / ∞`
                        : `${used.toLocaleString()} / ${limit.toLocaleString()}`}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        disabled={resetMut.isPending || limit === null}
                        onClick={() => {
                          if (
                            window.confirm(
                              t("app.admin.detail.quota.resetConfirm").replace(
                                "{feature}",
                                key,
                              ),
                            )
                          ) {
                            resetMut.mutate(key, { onSuccess: onResetSuccess });
                          }
                        }}
                        className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2.5 py-1 text-xs font-medium hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
                        title={
                          limit === null
                            ? t("app.admin.detail.quota.unlimitedHint")
                            : undefined
                        }
                      >
                        {t("app.admin.detail.quota.resetCta")}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {resetMut.isSuccess && (
        <p className="mt-2 text-xs text-emerald-700">
          {t("app.admin.detail.quota.resetSuccess").replace(
            "{feature}",
            resetMut.data?.feature ?? "",
          )}
        </p>
      )}
      {resetMut.isError && (
        <p className="mt-2 text-xs text-destructive">{(resetMut.error as Error).message}</p>
      )}
    </div>
  );
}

function EntitlementsSection({ entitlements }: { entitlements: AdminEntitlements }) {
  const t = useTranslations();
  return (
    <section className="rounded-md border border-border bg-card p-4">
      <h2 className="text-lg font-semibold">{t("app.admin.detail.entitlements.title")}</h2>
      <p className="text-sm text-muted-foreground">
        {t("app.admin.detail.entitlements.desc").replace("{plan}", entitlements.planId)}
      </p>

      <div className="mt-3 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <KeyValueList
          title={t("app.admin.detail.entitlements.toggles")}
          rows={Object.entries(entitlements.toggles).map(([k, v]) => [k, v ? "true" : "false"])}
        />
        <KeyValueList
          title={t("app.admin.detail.entitlements.quotas")}
          rows={Object.entries(entitlements.quotas).map(([k, v]) => [
            k,
            v === null ? "∞" : String(v),
          ])}
        />
        <KeyValueList
          title={t("app.admin.detail.entitlements.enums")}
          rows={Object.entries(entitlements.enums).map(([k, v]) => [k, v])}
        />
      </div>
    </section>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border py-1.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-right">{value}</dd>
    </div>
  );
}

function KeyValueList({
  title,
  rows,
}: {
  title: string;
  rows: ReadonlyArray<readonly [string, string]>;
}) {
  return (
    <div>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      <ul className="space-y-1 text-xs">
        {rows.length === 0 ? (
          <li className="text-muted-foreground">—</li>
        ) : (
          rows.map(([key, value]) => (
            <li
              key={key}
              className="flex items-baseline justify-between gap-2 border-b border-border py-1"
            >
              <span className="font-mono">{key}</span>
              <span>{value}</span>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}

function parseOverrideValue(
  kind: OverrideKind,
  raw: string,
): boolean | number | string | null {
  if (kind === "TOGGLE") return raw.trim().toLowerCase() === "true";
  if (kind === "QUOTA") {
    const trimmed = raw.trim();
    if (trimmed.length === 0) return null;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
  }
  return raw;
}
